/**
 * AuditSummaryDetailsSheet — Side sheet for displaying full AI audit summary data
 *
 * Displays all fields from AuditAISummaryData in structured cards with
 * collapsible sections for issues/gaps.
 */

import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  FileText,
  AlertTriangle,
  ListChecks,
  Sparkles,
  ChevronDown,
  CheckCircle2,
  XCircle,
  ThumbsUp,
  ThumbsDown,
  Footprints,
  RefreshCw,
} from "lucide-react";
import { SideSheet, Badge, Tooltip } from "@/components/ui";
import { useTranslation } from "@/packages/utils";
import type { AuditAISummaryData } from "@/packages/types";

// ─── Props ───────────────────────────────────────────────────────────────────

interface AuditSummaryDetailsSheetProps {
  aiData: AuditAISummaryData | undefined;
  open: boolean;
  onClose: () => void;
  onGenerate?: () => void;
  isGenerating?: boolean;
  isUpToDate?: boolean;
}

// ─── Card Shell ───────────────────────────────────────────────────────────────

function InfoCard({
  children,
  className = "",
}: {
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <div
      className={`rounded-xl bg-card border border-border overflow-hidden ${className}`}
    >
      {children}
    </div>
  );
}

function CardHeader({ icon, title }: { icon: React.ReactNode; title: string }) {
  return (
    <div className="flex items-center gap-2 px-4 pt-4 pb-2">
      <span className="text-muted">{icon}</span>
      <h3 className="text-sm font-semibold text-primary">{title}</h3>
    </div>
  );
}

function CardBody({
  children,
  className = "",
}: {
  children: React.ReactNode;
  className?: string;
}) {
  return <div className={`px-4 pb-4 ${className}`}>{children}</div>;
}

// ─── Collapsible Section ──────────────────────────────────────────────────────

function CollapsibleSection({
  icon,
  label,
  count,
  items,
  accentColor,
}: {
  icon: React.ReactNode;
  label: string;
  count: number;
  items: string[];
  accentColor: string;
}) {
  const [open, setOpen] = useState(false);

  return (
    <div className="border border-border rounded-lg overflow-hidden">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center justify-between px-3 py-2.5 text-left hover:bg-card/50 transition-colors"
      >
        <div className="flex items-center gap-2">
          <span className={accentColor}>{icon}</span>
          <span className="text-sm font-medium text-primary">
            {label} <span className="text-muted font-normal">({count})</span>
          </span>
        </div>
        <motion.span
          animate={{ rotate: open ? 180 : 0 }}
          transition={{ duration: 0.2 }}
          className="text-muted"
        >
          <ChevronDown size={14} />
        </motion.span>
      </button>

      <AnimatePresence initial={false}>
        {open && (
          <motion.div
            key="content"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.2, ease: "easeInOut" }}
            className="overflow-hidden"
          >
            <ul className="px-3 pb-3 pt-1 space-y-1.5 border-t border-border">
              {items.map((item, idx) => (
                <li
                  key={`${idx}-${item.slice(0, 20)}`}
                  className="flex gap-2 text-sm text-secondary leading-relaxed"
                >
                  <span className={`${accentColor} shrink-0 mt-0.5`}>
                    &#9679;
                  </span>
                  <span>{item}</span>
                </li>
              ))}
            </ul>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

// ─── Coverage Indicator ───────────────────────────────────────────────────────

function CoverageIndicator({
  ok,
  label,
  description,
}: {
  ok: boolean;
  label: string;
  description?: string;
}) {
  const inner = (
    <div className="flex flex-col items-center gap-1.5 p-3 rounded-lg border border-border">
      {ok ? (
        <CheckCircle2 size={18} className="text-green-400" />
      ) : (
        <XCircle size={18} className="text-red-400" />
      )}
      <span className="text-xs font-medium text-secondary text-center leading-tight">
        {label}
      </span>
    </div>
  );

  if (description) {
    return <Tooltip content={description}>{inner}</Tooltip>;
  }
  return inner;
}

// ─── Bullet List ──────────────────────────────────────────────────────────────

function BulletList({
  items,
  accentClass = "text-accent",
}: {
  items: string[];
  accentClass?: string;
}) {
  return (
    <ul className="space-y-2">
      {items.map((item, idx) => (
        <li
          key={`${idx}-${item.slice(0, 20)}`}
          className="flex gap-2 text-sm text-secondary leading-relaxed"
        >
          <span className={`${accentClass} shrink-0`}>&#9679;</span>
          <span>{item}</span>
        </li>
      ))}
    </ul>
  );
}

// ─── Status Badge Colour Map ──────────────────────────────────────────────────

function statusBadgeColor(status: string): { bg: string; text: string } {
  const map: Record<string, { bg: string; text: string }> = {
    in_progress: {
      bg: "color-mix(in oklch, var(--color-accent) 15%, transparent)",
      text: "var(--color-accent)",
    },
    draft: { bg: "var(--color-card)", text: "var(--color-muted)" },
    submitted: {
      bg: "color-mix(in oklch, #f59e0b 15%, transparent)",
      text: "#f59e0b",
    },
    approved: {
      bg: "color-mix(in oklch, #4ade80 15%, transparent)",
      text: "#4ade80",
    },
    completed: {
      bg: "color-mix(in oklch, #4ade80 15%, transparent)",
      text: "#4ade80",
    },
    rejected: {
      bg: "color-mix(in oklch, #f87171 15%, transparent)",
      text: "#f87171",
    },
    archived: { bg: "var(--color-card)", text: "var(--color-muted)" },
    pending: { bg: "var(--color-card)", text: "var(--color-secondary)" },
  };
  return (
    map[status.toLowerCase()] ?? {
      bg: "var(--color-card)",
      text: "var(--color-secondary)",
    }
  );
}

// ─── Main Component ───────────────────────────────────────────────────────────

export function AuditSummaryDetailsSheet({
  aiData,
  open,
  onClose,
  onGenerate,
  isGenerating,
  isUpToDate,
}: AuditSummaryDetailsSheetProps) {
  const { t } = useTranslation(["audits", "common"]);

  if (!aiData) return null;

  const analysis = aiData.aiAnalysis;
  const metrics = aiData.computedMetrics;
  const canApprove = metrics?.canApprove;
  const isApproved = analysis?.approvalRecommendation.recommendation === "yes";

  const issuesAndGaps = analysis?.issuesAndGaps;
  const hasIssues =
    (issuesAndGaps?.missingElements?.length ?? 0) > 0 ||
    (issuesAndGaps?.inconsistencies?.length ?? 0) > 0 ||
    (issuesAndGaps?.areasForImprovement?.length ?? 0) > 0;

  return (
    <SideSheet open={open} onClose={onClose}>
      {/* Header */}
      <SideSheet.Header
        title={t("audits:overview.aiSummary.title")}
        icon={<Sparkles size={18} className="text-accent" />}
      />

      {/* Body */}
      <SideSheet.Body className="overflow-y-auto">
        <div className="space-y-3">
          {/* Regenerating banner */}
          {isGenerating && (
            <div className="rounded-lg bg-accent/10 border border-accent/20 px-4 py-3 flex items-center gap-2 animate-pulse">
              <RefreshCw size={14} className="text-accent animate-spin" />
              <span className="text-sm text-accent font-medium">
                {t("audits:overview.sheet.regenerating")}
              </span>
            </div>
          )}
          {/* ── Summary Hero ─────────────────────────────────────── */}
          <InfoCard>
            <CardHeader
              icon={<FileText size={14} />}
              title={t("audits:overview.aiSummary.summary")}
            />
            <CardBody>
              <p className="text-sm text-secondary leading-relaxed">
                {aiData.summary}
              </p>
            </CardBody>
          </InfoCard>

          {/* ── Approval Recommendation ───────────────────────────── */}
          {analysis && (
            <div
              className={`rounded-xl border overflow-hidden ${
                isApproved
                  ? "border-green-500/20 bg-green-500/5"
                  : "border-red-500/20 bg-red-500/5"
              }`}
            >
              <div className="flex items-center gap-2 px-4 pt-4 pb-2">
                <span
                  className={isApproved ? "text-green-400" : "text-red-400"}
                >
                  {isApproved ? (
                    <ThumbsUp size={14} />
                  ) : (
                    <ThumbsDown size={14} />
                  )}
                </span>
                <h3 className="text-sm font-semibold text-primary">
                  {t("audits:overview.sheet.approvalRecommendation")}
                </h3>
              </div>
              <div className="px-4 pb-4 space-y-3">
                <div className="flex items-center gap-2 flex-wrap">
                  <Badge
                    color={
                      isApproved
                        ? { bg: "rgba(74,222,128,0.15)", text: "#4ade80" }
                        : { bg: "rgba(248,113,113,0.15)", text: "#f87171" }
                    }
                  >
                    {isApproved
                      ? t("audits:overview.aiSummary.approved")
                      : t("audits:overview.aiSummary.notApproved")}
                  </Badge>
                  {canApprove && (
                    <Badge
                      color={{ bg: "rgba(74,222,128,0.15)", text: "#4ade80" }}
                    >
                      <CheckCircle2 size={10} className="mr-1 inline" />
                      {t("audits:overview.sheet.canApprove")}
                    </Badge>
                  )}
                </div>

                {/* Reasoning */}
                <p className="text-sm text-secondary leading-relaxed">
                  {analysis.approvalRecommendation.reasoning}
                </p>

                {/* Conditions for approval */}
                {analysis.approvalRecommendation.conditionsForApproval &&
                  analysis.approvalRecommendation.conditionsForApproval.length >
                    0 && (
                    <div className="pt-2 border-t border-border/50">
                      <p className="text-xs font-medium text-muted uppercase tracking-wider mb-2">
                        {t("audits:overview.sheet.conditionsForApproval")}
                      </p>
                      <BulletList
                        items={
                          analysis.approvalRecommendation.conditionsForApproval
                        }
                      />
                    </div>
                  )}
              </div>
            </div>
          )}

          {/* ── Status Assessment ─────────────────────────────────── */}
          {metrics?.statusAssessment && (
            <InfoCard>
              <CardHeader
                icon={<AlertTriangle size={14} />}
                title={t("audits:overview.aiSummary.statusAssessment")}
              />
              <CardBody className="space-y-3">
                <Badge
                  color={statusBadgeColor(metrics.statusAssessment.status)}
                >
                  {metrics.statusAssessment.status}
                </Badge>
                {analysis?.statusExplanation && (
                  <p className="text-sm text-secondary leading-relaxed">
                    {analysis.statusExplanation}
                  </p>
                )}
              </CardBody>
            </InfoCard>
          )}

          {/* ── Document Coverage ─────────────────────────────────── */}
          {metrics?.documentCoverage && (
            <InfoCard>
              <CardHeader
                icon={<FileText size={14} />}
                title={t("audits:overview.aiSummary.documentCoverage")}
              />
              <CardBody className="space-y-3">
                <div className="grid grid-cols-3 gap-2">
                  <CoverageIndicator
                    ok={metrics.documentCoverage.isSufficient}
                    label={t("audits:overview.sheet.sufficient")}
                    description={t(
                      "audits:overview.sheet.sufficientDescription",
                    )}
                  />
                  <CoverageIndicator
                    ok={metrics.documentCoverage.isComplete}
                    label={t("audits:overview.sheet.complete")}
                    description={t("audits:overview.sheet.completeDescription")}
                  />
                  <CoverageIndicator
                    ok={metrics.documentCoverage.isAppropriate}
                    label={t("audits:overview.sheet.appropriate")}
                    description={t(
                      "audits:overview.sheet.appropriateDescription",
                    )}
                  />
                </div>
                {analysis?.documentCoverageAssessment && (
                  <p className="text-sm text-secondary leading-relaxed">
                    {analysis.documentCoverageAssessment}
                  </p>
                )}
              </CardBody>
            </InfoCard>
          )}

          {/* ── Issues & Gaps ─────────────────────────────────────── */}
          {issuesAndGaps && (
            <InfoCard>
              <CardHeader
                icon={<AlertTriangle size={14} />}
                title={t("audits:overview.sheet.issuesAndGaps")}
              />
              <CardBody className="space-y-2">
                {hasIssues ? (
                  <>
                    {issuesAndGaps.missingElements &&
                      issuesAndGaps.missingElements.length > 0 && (
                        <CollapsibleSection
                          icon={<XCircle size={14} />}
                          label={t("audits:overview.aiSummary.missingElements")}
                          count={issuesAndGaps.missingElements.length}
                          items={issuesAndGaps.missingElements}
                          accentColor="text-red-400"
                        />
                      )}
                    {issuesAndGaps.inconsistencies &&
                      issuesAndGaps.inconsistencies.length > 0 && (
                        <CollapsibleSection
                          icon={<AlertTriangle size={14} />}
                          label={t("audits:overview.aiSummary.inconsistencies")}
                          count={issuesAndGaps.inconsistencies.length}
                          items={issuesAndGaps.inconsistencies}
                          accentColor="text-amber-400"
                        />
                      )}
                    {issuesAndGaps.areasForImprovement &&
                      issuesAndGaps.areasForImprovement.length > 0 && (
                        <CollapsibleSection
                          icon={<ListChecks size={14} />}
                          label={t(
                            "audits:overview.aiSummary.areasForImprovement",
                          )}
                          count={issuesAndGaps.areasForImprovement.length}
                          items={issuesAndGaps.areasForImprovement}
                          accentColor="text-blue-400"
                        />
                      )}
                  </>
                ) : (
                  <p className="text-sm text-secondary">
                    {t("audits:overview.sheet.noIssuesOrGaps")}
                  </p>
                )}
              </CardBody>
            </InfoCard>
          )}

          {/* ── Next Steps ────────────────────────────────────────── */}
          {(() => {
            const steps = analysis?.nextSteps ?? aiData.nextSteps;
            if (!steps || steps.length === 0) return null;
            return (
              <InfoCard>
                <CardHeader
                  icon={<ListChecks size={14} />}
                  title={t("audits:overview.aiSummary.nextSteps")}
                />
                <CardBody>
                  <ol className="space-y-2 list-none">
                    {steps.map((step, idx) => (
                      <li
                        key={`${idx}-${step.slice(0, 20)}`}
                        className="flex gap-3 text-sm text-secondary leading-relaxed"
                      >
                        <span className="text-accent font-semibold shrink-0 w-4 text-right">
                          {idx + 1}.
                        </span>
                        <span>{step}</span>
                      </li>
                    ))}
                  </ol>
                </CardBody>
              </InfoCard>
            );
          })()}

          {/* ── Risks ─────────────────────────────────────────────── */}
          {aiData.risks && aiData.risks.length > 0 && (
            <div className="rounded-xl border border-amber-500/20 bg-amber-500/5 overflow-hidden">
              <div className="flex items-center gap-2 px-4 pt-4 pb-2">
                <span className="text-amber-400">
                  <AlertTriangle size={14} />
                </span>
                <h3 className="text-sm font-semibold text-primary">
                  {t("audits:overview.sheet.risks")}
                </h3>
              </div>
              <div className="px-4 pb-4">
                <BulletList items={aiData.risks} accentClass="text-amber-400" />
              </div>
            </div>
          )}

          {/* ── Key Milestones ────────────────────────────────────── */}
          {aiData.keyMilestones && aiData.keyMilestones.length > 0 && (
            <InfoCard>
              <CardHeader
                icon={<ListChecks size={14} />}
                title={t("audits:overview.aiSummary.keyMilestones")}
              />
              <CardBody>
                <BulletList items={aiData.keyMilestones} />
              </CardBody>
            </InfoCard>
          )}

          {/* ── Remaining Work ────────────────────────────────────── */}
          {aiData.remainingWork && aiData.remainingWork.length > 0 && (
            <InfoCard>
              <CardHeader
                icon={<Footprints size={14} />}
                title={t("audits:overview.aiSummary.remainingWork")}
              />
              <CardBody>
                <BulletList items={aiData.remainingWork} />
              </CardBody>
            </InfoCard>
          )}
        </div>
      </SideSheet.Body>

      {/* Footer */}
      <SideSheet.Footer>
        <div className="flex items-center justify-between w-full">
          {isUpToDate ? (
            <span className="text-xs text-muted italic">
              {t("audits:overview.sheet.upToDate")}
            </span>
          ) : (
            onGenerate && (
              <button
                type="button"
                onClick={onGenerate}
                disabled={isGenerating}
                className="inline-flex items-center gap-1.5 text-sm text-secondary hover:text-accent transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
              >
                <RefreshCw
                  size={13}
                  className={isGenerating ? "animate-spin" : ""}
                />
                {t("audits:overview.sheet.regenerate")}
              </button>
            )
          )}
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-secondary hover:text-primary transition-colors ml-auto"
          >
            {t("common:buttons.close")}
          </button>
        </div>
      </SideSheet.Footer>
    </SideSheet>
  );
}
